import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'
import { Accordion } from 'components/shared/Accordion/Accordion'
import { ScoreChip } from 'components/shared/ScoreChip/ScoreChip'
import cx from 'classnames'
import { Button } from 'components/shared/Button/Button'
import { HideExtensiveText } from 'components/shared/HideExtensiveText/HideExtensiveText'
import { percentScore } from 'utils/stringFormat'
import { getInterviewScoreTreshholds } from 'utils/score'
import { useInternalState } from './hooks'
import { Box } from 'components/layout/Box/Box'
import styles from './Reviews.module.scss'

const getQuestionTitle = (sections, sectionKey, questionId) => {
  const section = R.find(R.propEq('key', sectionKey), sections ?? [])
  const question = R.find(
    q => String(q.id) === String(questionId),
    section?.questions ?? [],
  )
  return question?.title ?? null
}

export const Reviews = ({ evaluators, sections = [], skills }) => {
  const skillsArray = R.toPairs(skills)

  const {
    state: { allReviewsOpen, reviewsOpenState },
    handlers: { toggleAllReviews, toggleReview },
  } = useInternalState({ skillsArray: R.map(([key]) => key, skillsArray) })

  const getReviews = selectedSkills =>
    R.map(
      ([key, { reviews, score, title }]) => (
        <Accordion
          key={key}
          className={styles.accordion}
          isInitiallyOpen={reviewsOpenState[key]}
        >
          <Accordion.Header
            className={styles.scoresHeader}
            onToggle={() => toggleReview(key)}
            title={
              <span className={styles.scoresTitle}>
                <span>{title}</span>
                <ScoreChip
                  score={percentScore(score)}
                  theme={getInterviewScoreTreshholds(score)}
                  size="medium"
                />
              </span>
            }
            buttonClassName={styles.button}
          />
          <Accordion.Panel className={styles.panel}>
            <div className={styles.responses}>
              {R.pipe(
                R.toPairs,
                R.map(([email, { comment, comments = {}, score }]) => {
                  const name = R.prop(
                    'name',
                    R.find(evaluator => email === evaluator.email, evaluators),
                  )
                  const questionComments = R.pipe(
                    R.toPairs,
                    R.reject(([, text]) => !text || !String(text).trim()),
                  )(comments)
                  const hasQuestionNotes = R.gt(R.length(questionComments), 0)
                  const hasLegacyComment = Boolean(comment)

                  return (
                    <div key={email} className={styles.response}>
                      {name ? (
                        <span className={styles.responseName}>{name}</span>
                      ) : null}
                      <span className={styles.responseScore}>
                        <ScoreChip
                          score={percentScore(score)}
                          className={styles.responseChip}
                          size="small"
                        />
                      </span>
                      <div className={styles.responseComments}>
                        {hasQuestionNotes ? (
                          R.map(([questionId, noteText]) => {
                            const questionTitle = getQuestionTitle(
                              sections,
                              key,
                              questionId,
                            )
                            return (
                              <div
                                key={questionId}
                                className={styles.responseCommentBlock}
                              >
                                {questionTitle ? (
                                  <span className={styles.responseCommentLabel}>
                                    {questionTitle}
                                  </span>
                                ) : null}
                                <span className={styles.responseComment}>
                                  <HideExtensiveText text={noteText} />
                                </span>
                              </div>
                            )
                          }, questionComments)
                        ) : hasLegacyComment ? (
                          <span className={styles.responseComment}>
                            <HideExtensiveText text={comment} />
                          </span>
                        ) : (
                          <span
                            className={cx(styles.responseComment, {
                              [styles.noComment]: true,
                            })}
                          >
                            {t`no comment`}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                }),
              )(reviews)}
            </div>
          </Accordion.Panel>
        </Accordion>
      ),
      selectedSkills,
    )

  return (
    <Box direction="column" className={styles.reviews}>
      <Button
        label={allReviewsOpen ? t`Hide all` : t`Show all`}
        size="small"
        variant="text"
        className={styles.showButton}
        onClick={toggleAllReviews}
      />
      <Box className={styles.scores} justify="space-between">
        <Box direction="column" className={styles.column}>
          {getReviews(skillsArray.filter((_, index) => index % 2 === 0))}
        </Box>
        <Box direction="column" className={styles.column}>
          {getReviews(skillsArray.filter((_, index) => index % 2 === 1))}
        </Box>
      </Box>
    </Box>
  )
}
